import React, { useEffect } from "react";
import {
    BrowserRouter as Router,
    Route,
    Switch,
    Redirect,
    useLocation,
} from "react-router-dom";

import { UserProvider, useUser } from "./components/Context/UserContext";
import { ThemeProvider } from "./components/Context/ThemeContext";
import { UIProvider } from "./components/Context/UIContext";
import NavMenu from "./components/NavMenu";
import Login from "./components/Login";
import routes, { allRoutes } from "./routesConfig";
import Sidebar from "./components/Sidebar";
import EmployeeDetailPage from "./components/EmployeeDetailPage";
import TokenLogin from "./components/TokenLogin";
import "./index.css";

const APP_NAME = "Skill Management · Mindteck";

/*
 * SM-ACX-013 / SM-GEN-002
 *
 * Individual pages used to assign document.title on mount and never clean it
 * up, so the tab kept whatever the last visited report had set — the login
 * page famously still read "Admin Report". The title is now derived from the
 * route in one place, which means every view has a descriptive title and none
 * can go stale.
 */
function DocumentTitle() {
    const location = useLocation();

    useEffect(() => {
        const path = (location.pathname || "/").toLowerCase();

        const match = allRoutes.find(r => {
            const routePath = r.path.toLowerCase();
            // "/employee/:employeeId" → matches "/employee/IBE2594"
            if (routePath.includes("/:")) {
                return path.startsWith(routePath.split("/:")[0] + "/");
            }
            return routePath === path;
        });

        const pageTitle =
            match?.title ||
            (path === "/login" ? "Sign in" : null) ||
            (path === "/" ? "Home" : null);

        document.title = pageTitle ? `${pageTitle} | ${APP_NAME}` : APP_NAME;
    }, [location.pathname]);

    return null;
}

/*
 * Once the user signs out, the login screen becomes a terminal page: Back and
 * Forward must not walk back into the pages of the session that just ended.
 * Re-pushing the current entry on every popstate pins the address bar there
 * (the initial push also drops any forward entries), so the buttons become
 * no-ops until the user signs in again.
 */
function SignedOutHistoryGuard() {
    const { empId, signedOut } = useUser();
    const location = useLocation();
    const active = signedOut && !empId;

    useEffect(() => {
        if (!active) return undefined;

        const pin = () =>
            window.history.pushState(null, document.title, window.location.href);

        pin();
        window.addEventListener("popstate", pin);
        return () => window.removeEventListener("popstate", pin);
    }, [active, location.pathname]);

    return null;
}

function AppContent() {
    const { empId, menus } = useUser();

    /*
     * A menu entry the server sends without a routePath is a group heading
     * ("Skill Set Reports"). It gets the group's landing route so the menu item
     * opens a real page instead of going nowhere (SM-HR-009, SM-GEN-003).
     */
    const routeForMenu = menu => {
        const direct = allRoutes.find(r => r.label === menu.menuName);
        if (direct) return direct;

        const group = routes.find(
            r => r.children && r.label === menu.menuName && r.element
        );
        return group || null;
    };

    return (
        <Router>
            <SignedOutHistoryGuard />
            <DocumentTitle />

            {!empId && (
                /* Any protected URL still in the address bar is replaced (not
                   pushed) with /login, so a signed-out session never leaves a
                   reachable history entry behind. */
                <Switch>
                    <Route exact path="/token-login" component={TokenLogin} />
                    <Route exact path="/login" component={Login} />
                    <Redirect to="/login" />
                </Switch>
            )}

            {empId && (
                <>
                    <header role="banner">
                        <NavMenu />
                    </header>

                    <div className="app-layout">

                        <nav role="navigation" aria-label="Main Navigation">
                            <Sidebar />
                        </nav>

                        <main
                            role="main"
                            id="main-content"
                            className="main-content"
                        >
                            <Switch>

                                <Route
                                    path="/employee/:employeeId"
                                    component={EmployeeDetailPage}
                                />

                                {menus.map(menu => {
                                    const route = routeForMenu(menu);

                                    if (!route) return null;

                                    return (
                                        <Route
                                            key={route.path}
                                            exact
                                            path={route.path}
                                            render={() => <route.element />}
                                        />
                                    );
                                })}

                                <Redirect from="*" to="/home" />

                            </Switch>
                        </main>

                    </div>

                    <footer role="contentinfo">
                       
                    </footer>
                </>
            )}
        </Router>
    );
}

export default function App() {
    return (
        <ThemeProvider>
            <UIProvider>
                <UserProvider>
                    <AppContent />
                </UserProvider>
            </UIProvider>
        </ThemeProvider>
    );
}